/**
 * One Pandia (edge box), for the integration engineer: where it is on the LAN, what it listens on, its DICOM and TLS
 * settings, the certificate instruments must trust, pending actions, install commands and its event log.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowClockwise,
  Certificate,
  Clock,
  DownloadSimple,
  Gear,
  HardDrives,
  Lightning,
  ListChecks,
  Planet,
  RocketLaunch,
  ShieldCheck,
  TerminalWindow,
  WifiHigh,
} from "@phosphor-icons/react";
import {
  EdgeHealth,
  AE_PATTERN,
  CHANNELS,
  downloadFile,
  Edge,
  EdgeAction,
  EdgeDetail as Detail,
  EdgeSettings,
  EdgeUpdates,
  fieldErrors,
  http,
  isNotFound,
  TlsCertificate,
} from "../api";
import { useSession } from "../AuthToken";
import { ConnectionDots, Mono, StatusChip } from "../ui";
import { caps, fonts, tones } from "../theme";
import { EventLog } from "../console/EventLog";
import {
  ago,
  BackLink,
  CodeBlock,
  Confirm,
  CopyValue,
  Fact,
  formatFingerprint,
  NotYet,
  Panel,
  Tag,
} from "../console/common";

const SKEW_WARN_MS = 120_000;

/** A detail record when the server has the endpoint; otherwise the row from the list, so the page still says something useful. */
type Loaded = { kind: "full"; edge: Detail } | { kind: "basic"; edge: Edge };

export default function EdgeDetail() {
  const { id = "" } = useParams();
  const { token, me } = useSession();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    const r = await http.get<Detail>(token, `/admin/edges/${id}`);
    if (r.ok) {
      setLoaded({ kind: "full", edge: r.result });
      setError("");
      return;
    }
    if (!isNotFound(r.error)) return setError(r.error);
    // Older servers: fall back to the list so the header and basics still render.
    const list = await http.get<Edge[]>(token, "/admin/edges");
    const row = list.ok ? list.result.find((e) => e.id === id) : undefined;
    if (row) setLoaded({ kind: "basic", edge: row });
    else setError(list.ok ? "This edge was not found." : list.error);
  }, [token, id]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15_000);
    return () => clearInterval(iv);
  }, [load]);

  if (me && me.role !== "admin") return <Alert severity="info">The implementer console requires the admin role.</Alert>;
  if (!loaded)
    return error ? (
      <Stack spacing={2}>
        <BackLink to="/admin?tab=edges" label="Pandia" />
        <Alert severity="error">{error}</Alert>
      </Stack>
    ) : null;

  const e = loaded.edge;
  const full = loaded.kind === "full" ? loaded.edge : null;
  return (
    <Stack spacing={3}>
      <BackLink to="/admin?tab=edges" label="Pandia" />
      {error && (
        <Alert severity="warning" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <Header edge={e} />
      {!full && (
        <Alert severity="info">
          Detailed edge settings are not available on this server yet. Basic details are shown from the edge list.
          Settings, TLS and live health appear after the server is updated.
        </Alert>
      )}
      {full && (
        <>
          <Overview edge={full} />
          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={7}>
              <SettingsForm edge={full} onSaved={(d) => setLoaded({ kind: "full", edge: d })} onReload={load} />
            </Grid>
            <Grid item xs={12} lg={5}>
              <Stack spacing={2.5}>
                {full.updates && <UpdatesPanel edge={full} u={full.updates} onChanged={load} />}
                <TlsCard edge={full} onChanged={load} />
                <PendingActions edge={full} />
              </Stack>
            </Grid>
          </Grid>
          <LiveStreams edge={full} />
          <CollectedLogs edge={full} />
          <Panel title="Install and upgrade" icon={<TerminalWindow size={16} />}>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Run these commands as root on the edge. Upgrading keeps the enrollment, settings and certificate.
              </Typography>
              {full.installCommand && <CodeBlock label="Install" text={full.installCommand} />}
              {full.upgradeCommand && <CodeBlock label="Upgrade" text={full.upgradeCommand} />}
            </Stack>
          </Panel>
        </>
      )}
      <EventLog deviceId={e.id} title="Event log for this edge" />
    </Stack>
  );
}

function Header({ edge: e }: { edge: Detail | Edge }) {
  const state = e.revokedAt ? "idle" : e.online ? "live" : "failed";
  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, position: "relative", overflow: "hidden", borderRadius: "14px" }}>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: -120,
          top: -160,
          width: 360,
          height: 360,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(tones.ochre, 0.12)}, transparent 65%)`,
          pointerEvents: "none",
        }}
      />
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        alignItems={{ md: "center" }}
        sx={{ position: "relative" }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="overline" sx={{ color: "primary.main", lineHeight: 1.6 }}>
              Edge
            </Typography>
            {e.revokedAt && <StatusChip status="revoked" />}
          </Stack>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: 26, md: 32 }, wordBreak: "break-word" }}>
            {e.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {e.tenant.code} / {e.site?.name ?? e.siteCode}
            {e.hostname && (
              <>
                {" "}
                · <Mono>{e.hostname}</Mono>
              </>
            )}
            {e.arch && <> · {e.arch}</>}
          </Typography>
        </Box>
        <Stack spacing={0.75} sx={{ borderLeft: { md: 1 }, borderColor: { md: "divider" }, pl: { md: 3 } }}>
          <ConnectionDots
            state={state}
            from={<HardDrives size={20} />}
            to={<Planet size={20} />}
            dots={8}
            size={5}
            label={e.revokedAt ? "revoked" : e.online ? "online" : "offline"}
          />
          <Typography variant="caption" color="text.secondary">
            Last check-in {ago(e.lastSeenAt)}
            {e.lastSeenAt ? ` · ${new Date(e.lastSeenAt).toLocaleString()}` : ""}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

function fmtSkew(ms: number) {
  const abs = Math.abs(ms);
  const sign = ms > 0 ? "+" : ms < 0 ? "-" : "";
  if (abs < 1000) return `${sign}${Math.round(abs)} ms`;
  if (abs < 120_000) return `${sign}${(abs / 1000).toFixed(1)} s`;
  if (abs < 7_200_000) return `${sign}${Math.round(abs / 60_000)} min`;
  return `${sign}${(abs / 3_600_000).toFixed(1)} h`;
}

function Overview({ edge }: { edge: Detail }) {
  const h = edge.health;
  if (!h) {
    return (
      <Paper sx={{ p: 2.5, borderRadius: "14px" }}>
        <NotYet>
          No health report from this Pandia yet. LAN addresses, versions and clock skew appear after its next check-in.
        </NotYet>
      </Paper>
    );
  }
  const skew = h.clockSkewMs;
  const skewBad = skew !== null && Math.abs(skew) > SKEW_WARN_MS;
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} lg={3}>
        <Panel title="LAN addresses" icon={<WifiHigh size={16} />} sx={{ height: "100%" }}>
          <Stack spacing={0.75}>
            {h.lanAddresses.length ? (
              h.lanAddresses.map((a) => <CopyValue key={a} value={a} />)
            ) : (
              <Typography variant="body2" color="text.secondary">
                None reported
              </Typography>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            Point instruments at one of these addresses.
          </Typography>
        </Panel>
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Panel title="Listening" icon={<ShieldCheck size={16} />} sx={{ height: "100%" }}>
          <Stack spacing={1.25}>
            <Fact label="DICOM (plain)">
              <CopyValue value={String(h.listening.dicom)} />
            </Fact>
            <Fact label="DICOM TLS">
              {h.listening.dicomTls ? (
                <CopyValue value={String(h.listening.dicomTls)} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  off
                </Typography>
              )}
            </Fact>
            {Object.keys(h.components).length > 0 && (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {Object.entries(h.components).map(([k, v]) => (
                  <StatusChip
                    key={k}
                    status={v}
                    tone={
                      /^(ok|listening|running|ready)$/i.test(v) ? "ok" : /off|disabled/i.test(v) ? "neutral" : "warn"
                    }
                    label={`${k}: ${v}`}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Panel>
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Panel title="Versions" icon={<Gear size={16} />} sx={{ height: "100%" }}>
          <Stack spacing={1.25}>
            <Fact label="Agent">
              <Mono>{h.versions.agent ?? edge.agentVersion ?? "-"}</Mono>
            </Fact>
            <Fact label="Orthanc">
              <Mono>{h.versions.orthanc ?? "-"}</Mono>
            </Fact>
            <Fact label="MediaMTX">
              <Mono>{h.versions.mediamtx ?? "-"}</Mono>
            </Fact>
          </Stack>
        </Panel>
      </Grid>
      <Grid item xs={12} sm={6} lg={3}>
        <Panel
          title="Clock"
          icon={<Clock size={16} />}
          sx={{ height: "100%", borderColor: skewBad ? alpha(tones.warn, 0.4) : undefined }}
        >
          <Typography
            sx={{
              fontFamily: fonts.display,
              fontWeight: 200,
              fontSize: 30,
              lineHeight: 1.2,
              color: skewBad ? tones.warn : "text.primary",
            }}
          >
            {skew === null ? "unknown" : fmtSkew(skew)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Edge clock minus Jupiter clock
          </Typography>
          {skewBad ? (
            <Stack spacing={1}>
              <StatusChip status="warn" tone="warn" label="clock skew beyond 2 min" />
              <Typography variant="caption" sx={{ color: "text.primary", display: "block" }}>
                Instrument worklist "Today" filters depend on this clock. A skewed edge can show the wrong day's
                patients near midnight and stamp stores with the wrong time. Check the network time (NTP)
                synchronization on the edge.
              </Typography>
            </Stack>
          ) : (
            skew !== null && <StatusChip status="ok" label="in sync" />
          )}
        </Panel>
      </Grid>
      {h.storage && (
        <Grid item xs={12}>
          <StoragePanel s={h.storage} />
        </Grid>
      )}
    </Grid>
  );
}

const gb = (n: number | null) =>
  n === null ? "-" : n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(1)} GB` : `${Math.round(n / 1024 ** 2)} MB`;

// The edge is a buffer, not an archive: what it holds locally, what is still on its way to Jupiter, and when it cleans up.
function StoragePanel({ s }: { s: NonNullable<EdgeHealth["storage"]> }) {
  const used = s.diskTotalBytes && s.diskFreeBytes !== null ? 1 - s.diskFreeBytes / s.diskTotalBytes : null;
  const full = used !== null && used > 0.85;
  return (
    <Panel
      title="Storage and uploads"
      icon={<HardDrives size={16} />}
      sx={{ borderColor: full ? alpha(tones.warn, 0.4) : undefined }}
    >
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <Fact label="Disk free">
            <Typography variant="body2" sx={{ color: full ? tones.warn : undefined }}>
              {gb(s.diskFreeBytes)} of {gb(s.diskTotalBytes)}
            </Typography>
            {used !== null && (
              <Typography variant="caption" color="text.secondary">
                {Math.round(used * 100)}% used
              </Typography>
            )}
          </Fact>
        </Grid>
        <Grid item xs={6} md={3}>
          <Fact label="Held locally">
            <Typography variant="body2">
              {s.orthancInstances ?? "-"} objects, {gb(s.orthancBytes)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Deleted {s.retentionDays} days after Jupiter confirms receipt
            </Typography>
          </Fact>
        </Grid>
        <Grid item xs={6} md={3}>
          <Fact label="Uploads in progress">
            <Typography variant="body2">{s.uploadsInProgress}</Typography>
            <Typography variant="caption" color="text.secondary">
              Files over {gb(s.directToS3AboveBytes)} go straight to storage in parts and resume after outages
            </Typography>
          </Fact>
        </Grid>
        <Grid item xs={6} md={3}>
          <Fact label="Last upload">
            <Typography variant="body2">{s.lastUploadAt ? ago(s.lastUploadAt) : "none yet"}</Typography>
            {s.lastUploadError && (
              <Typography variant="caption" sx={{ color: tones.warn, display: "block" }}>
                Last error: {s.lastUploadError}
              </Typography>
            )}
          </Fact>
        </Grid>
      </Grid>
      {full && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          The edge disk is over 85% full. Check the internet connection and the upload backlog; uploads that cannot
          reach Jupiter stay on the edge.
        </Alert>
      )}
    </Panel>
  );
}

const LOG_LEVELS: { value: EdgeSettings["logLevel"]; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "verbose", label: "Verbose" },
  { value: "trace", label: "Trace" },
];

function SettingsForm({
  edge,
  onSaved,
  onReload,
}: {
  edge: Detail;
  onSaved: (d: Detail) => void;
  onReload: () => void;
}) {
  const { token } = useSession();
  const [v, setV] = useState<EdgeSettings>(edge.settings);
  const [base, setBase] = useState<EdgeSettings>(edge.settings);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ tone: "success" | "error" | "info"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<EdgeAction | null>(null);

  // Pick up server changes unless the form has unsaved edits.
  useEffect(() => {
    if (JSON.stringify(v) === JSON.stringify(base)) setV(edge.settings);
    setBase(edge.settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(edge.settings)]);

  const dirty = (Object.keys(v) as (keyof EdgeSettings)[]).filter((k) => v[k] !== base[k]);
  const portErr = (n: number) => (!Number.isInteger(n) || n < 1 || n > 65535 ? "Enter a port from 1 to 65535." : "");
  const local: Record<string, string> = {
    edgeAeTitle: AE_PATTERN.test(v.edgeAeTitle)
      ? ""
      : "Up to 16 characters from A-Z, 0-9, underscore, hyphen and period.",
    dicomPort: portErr(v.dicomPort),
    dicomTlsPort: v.dicomTlsEnabled
      ? portErr(v.dicomTlsPort) || (v.dicomTlsPort === v.dicomPort ? "Must be different from the plain port." : "")
      : "",
  };
  const invalid = Object.values(local).some(Boolean);
  const err = (k: string) => local[k] || errs[k] || "";

  const save = async () => {
    setBusy(true);
    setErrs({});
    const patch: Partial<EdgeSettings> = {};
    for (const k of dirty) (patch as Record<string, unknown>)[k] = v[k];
    const r = await http.patch<Detail>(token, `/admin/edges/${edge.id}/settings`, patch);
    setBusy(false);
    if (!r.ok) {
      const fe = fieldErrors(r.error);
      setErrs(fe);
      setMsg({ tone: "error", text: Object.keys(fe).length ? "Some fields need attention." : r.error });
      return;
    }
    setBase(r.result.settings);
    setV(r.result.settings);
    onSaved(r.result);
    setMsg({ tone: "success", text: "Saved. The edge picks this up within about 30 s, or use Apply now." });
  };

  const act = async (action: EdgeAction) => {
    setBusy(true);
    const r = await http.post<{ id: string }>(token, `/admin/edges/${edge.id}/actions`, { action });
    setBusy(false);
    setConfirm(null);
    setMsg(
      r.ok
        ? {
            tone: "info",
            text:
              action === "restart-dicom"
                ? "Restart requested. The edge restarts DICOM at its next check-in."
                : "Re-apply requested. The edge re-reads its configuration at its next check-in.",
          }
        : { tone: "error", text: r.error }
    );
    onReload();
  };

  const num = (k: "dicomPort" | "dicomTlsPort") => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV({ ...v, [k]: e.target.value === "" ? NaN : Number(e.target.value) });
  const monoInput = { sx: { fontFamily: fonts.mono } };

  return (
    <Panel
      title="DICOM settings"
      icon={<Gear size={16} />}
      sx={{ height: "100%" }}
      action={dirty.length > 0 && <Tag color={tones.ochre}>{dirty.length} unsaved</Tag>}
    >
      <Stack spacing={2.25}>
        {msg && (
          <Alert severity={msg.tone} onClose={() => setMsg(null)}>
            {msg.text}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Pandia AE title"
              value={v.edgeAeTitle}
              onChange={(e) => setV({ ...v, edgeAeTitle: e.target.value.toUpperCase() })}
              error={!!err("edgeAeTitle")}
              helperText={
                err("edgeAeTitle") ||
                "The AE title this Pandia answers to. Instruments enter it as the called AE title."
              }
              inputProps={{ maxLength: 16, spellCheck: false }}
              InputProps={monoInput}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="DICOM port (plain)"
              type="number"
              value={Number.isNaN(v.dicomPort) ? "" : v.dicomPort}
              onChange={num("dicomPort")}
              error={!!err("dicomPort")}
              helperText={err("dicomPort") || "Default: 4242"}
              InputProps={monoInput}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            p: 2,
            borderRadius: "12px",
            border: 1,
            borderColor: v.dicomTlsEnabled ? alpha(tones.live, 0.3) : "divider",
            bgcolor: v.dicomTlsEnabled ? alpha(tones.live, 0.04) : "transparent",
            transition: "all 0.3s",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={v.dicomTlsEnabled}
                onChange={(e) => setV({ ...v, dicomTlsEnabled: e.target.checked })}
                color="secondary"
              />
            }
            label={
              <Box>
                <Typography>DICOM TLS listener</Typography>
                <Typography variant="caption" color="text.secondary">
                  Runs beside the plain listener. Transport Layer Security (TLS) 1.2 or later, per DICOM PS3.15 section
                  B.12. Each instrument chooses which listener it uses.
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", m: 0, "& .MuiSwitch-root": { mt: -0.5 } }}
          />
          {v.dicomTlsEnabled && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="DICOM TLS port"
                  type="number"
                  value={Number.isNaN(v.dicomTlsPort) ? "" : v.dicomTlsPort}
                  onChange={num("dicomTlsPort")}
                  error={!!err("dicomTlsPort")}
                  helperText={err("dicomTlsPort") || "Default: 2762"}
                  InputProps={monoInput}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={v.tlsRequireClientCert}
                      onChange={(e) => setV({ ...v, tlsRequireClientCert: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Require client certificates</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Mutual TLS (mTLS). Instruments must present a certificate trusted on the instrument's own page
                        in Jupiter. FORUM needs this; HEYEX refuses it.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", m: 0 }}
                />
              </Grid>
            </Grid>
          )}
        </Box>
        <TextField
          select
          size="small"
          label="DICOM log level"
          value={v.logLevel}
          onChange={(e) => setV({ ...v, logLevel: e.target.value as EdgeSettings["logLevel"] })}
          helperText={
            v.logLevel === "default"
              ? "Errors, and one line per store."
              : v.logLevel === "verbose"
              ? "Logs every DICOM association, including worklist queries and C-ECHO requests. Use while connecting an instrument."
              : "Logs protocol data unit (PDU) detail. Produces a large volume of output. Set it back to Default when done."
          }
          error={!!errs.logLevel}
          sx={{ maxWidth: 420 }}
        >
          {LOG_LEVELS.map((l) => (
            <MenuItem key={l.value} value={l.value}>
              {l.label}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Button variant="contained" onClick={save} disabled={!dirty.length || invalid || busy}>
            Save
          </Button>
          {dirty.length > 0 && (
            <Button
              color="inherit"
              onClick={() => {
                setV(base);
                setErrs({});
              }}
            >
              Discard
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Ask the edge to re-read its configuration without restarting DICOM">
            <span>
              <Button
                size="small"
                color="inherit"
                startIcon={<ArrowClockwise size={14} />}
                onClick={() => setConfirm("reapply-config")}
                disabled={busy || !!edge.revokedAt}
              >
                Re-apply config
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Lightning size={15} />}
            onClick={() => setConfirm("restart-dicom")}
            disabled={busy || !!edge.revokedAt}
          >
            Apply now
          </Button>
        </Stack>
      </Stack>
      <Confirm
        open={confirm === "restart-dicom"}
        title="Apply now"
        confirm="Restart DICOM"
        busy={busy}
        onClose={() => setConfirm(null)}
        onConfirm={() => act("restart-dicom")}
      >
        <Typography variant="body2">
          The edge restarts its DICOM service with the saved settings at its next check-in (usually within seconds).
        </Typography>
        <Alert severity="warning">
          DICOM is unavailable for about 5 seconds. A store or worklist query in progress at that moment fails, and the
          instrument retries or shows an error. Avoid applying while a patient is being imaged.
        </Alert>
        {dirty.length > 0 && (
          <Alert severity="info">You have unsaved changes. Save first, or they will not be applied.</Alert>
        )}
      </Confirm>
      <Confirm
        open={confirm === "reapply-config"}
        title="Re-apply config"
        confirm="Re-apply"
        busy={busy}
        onClose={() => setConfirm(null)}
        onConfirm={() => act("reapply-config")}
      >
        <Typography variant="body2">
          The edge re-reads its settings and module configuration at its next check-in. Running associations are not
          interrupted.
        </Typography>
      </Confirm>
    </Panel>
  );
}

function daysUntil(iso: string) {
  return Math.floor((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function TlsCard({ edge, onChanged }: { edge: Detail; onChanged: () => void }) {
  const { token } = useSession();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [fresh, setFresh] = useState<TlsCertificate | null>(null);
  const tls = fresh ?? edge.tls;

  useEffect(() => setFresh(null), [edge.tls?.fingerprint]);

  const generate = async () => {
    setBusy(true);
    const r = await http.post<TlsCertificate & { pem: string }>(token, `/admin/edges/${edge.id}/tls-certificate`);
    setBusy(false);
    setConfirm(false);
    if (!r.ok) return setMsg({ tone: "error", text: r.error });
    setFresh({ fingerprint: r.result.fingerprint, subject: r.result.subject, notAfter: r.result.notAfter });
    setMsg({
      tone: "success",
      text: "New certificate issued. The edge installs it within about 30 s. Instruments that trust the old certificate need the new PEM file.",
    });
    onChanged();
  };
  const download = async () => {
    const err = await downloadFile(
      token,
      `/admin/edges/${edge.id}/tls-certificate.pem`,
      `${edge.name.replace(/[^A-Za-z0-9_.-]+/g, "-")}-dicom-tls.pem`
    );
    if (err) setMsg({ tone: "error", text: err });
  };

  const left = tls ? daysUntil(tls.notAfter) : null;
  return (
    <Panel
      title="TLS certificate"
      icon={<Certificate size={16} />}
      action={
        tls &&
        left !== null && (
          <StatusChip
            status={left < 0 ? "failed" : left < 30 ? "warn" : "ok"}
            tone={left < 0 ? "fail" : left < 30 ? "warn" : "ok"}
            label={left < 0 ? "expired" : `${left} days left`}
          />
        )
      }
    >
      <Stack spacing={2}>
        {msg && (
          <Alert severity={msg.tone} onClose={() => setMsg(null)}>
            {msg.text}
          </Alert>
        )}
        {tls ? (
          <>
            <Fact label="SHA-256 fingerprint">
              <CopyValue value={tls.fingerprint} display={formatFingerprint(tls.fingerprint)} />
            </Fact>
            <Fact label="Subject">
              <Mono sx={{ wordBreak: "break-all" }}>{tls.subject}</Mono>
            </Fact>
            <Fact label="Expires">{new Date(tls.notAfter).toLocaleString()}</Fact>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" startIcon={<DownloadSimple size={15} />} onClick={download}>
                Download PEM
              </Button>
              <Button
                color="inherit"
                startIcon={<ArrowClockwise size={14} />}
                onClick={() => setConfirm(true)}
                disabled={busy || !!edge.revokedAt}
              >
                Regenerate
              </Button>
            </Stack>
            <Box component="ol" sx={{ m: 0, pl: 2.5, color: "text.secondary", fontSize: 13, "& li": { mb: 0.5 } }}>
              <li>
                Download the PEM file and copy it to the instrument, by USB stick or through the instrument's web admin
                page.
              </li>
              <li>
                Import it on the instrument as a trusted certificate authority (CA) or server certificate for the
                storage and worklist connections.
              </li>
              <li>Point the instrument at the edge's DICOM TLS port, and turn on TLS for that connection.</li>
              <li>
                On the instrument's page in Jupiter, run Test connection. The TLS step shows the result of the
                handshake.
              </li>
            </Box>
          </>
        ) : (
          <>
            <NotYet compact>
              No certificate yet. Generate one before you turn on the TLS listener. Instruments need it to trust the
              edge.
            </NotYet>
            <Box>
              <Button
                variant="contained"
                startIcon={<Certificate size={15} />}
                onClick={generate}
                disabled={busy || !!edge.revokedAt}
              >
                Generate certificate
              </Button>
            </Box>
          </>
        )}
      </Stack>
      <Confirm
        open={confirm}
        title="Regenerate certificate"
        confirm="Regenerate"
        danger
        busy={busy}
        onClose={() => setConfirm(false)}
        onConfirm={generate}
      >
        <Typography variant="body2">This replaces the edge's DICOM TLS certificate with a new key pair.</Typography>
        <Alert severity="warning">
          Every instrument that trusts the current certificate fails its TLS handshake until the new PEM file is
          installed on it. Plain DICOM is not affected.
        </Alert>
      </Confirm>
    </Panel>
  );
}

/** Fleet updates for this box: what it runs, what it is moving to, and the two controls a practice has. */
function UpdatesPanel({ edge, u, onChanged }: { edge: Detail; u: EdgeUpdates; onChanged: () => void }) {
  const { token, me } = useSession();
  const [busy, setBusy] = useState(false);
  const [pause, setPause] = useState<{ until: string; reason: string } | null>(null);
  const [msg, setMsg] = useState("");
  const post = async (body: Record<string, unknown>) => {
    if (!token) return;
    setBusy(true);
    const r = await http.post(token, `/admin/edges/${edge.id}/updates`, body);
    setBusy(false);
    setMsg(r.ok ? "" : r.error);
    if (r.ok) {
      setPause(null);
      onChanged();
    }
  };
  const paused = !!u.pausedUntil && new Date(u.pausedUntil) > new Date();
  const tone = u.status === "ok" ? "ok" : u.status === "updating" ? "warn" : u.status ? "fail" : "neutral";
  const borderWarn = u.status === "failed" || u.status === "rolled-back";
  return (
    <Panel
      title="Updates"
      icon={<RocketLaunch size={16} />}
      sx={{ borderColor: borderWarn ? alpha(tones.warn, 0.4) : undefined }}
    >
      <Stack spacing={1.5}>
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Fact label="Running">
              {u.release ?? (
                <Typography variant="body2" color="text.secondary">
                  unknown (installed before releases)
                </Typography>
              )}
            </Fact>
          </Grid>
          <Grid item xs={6}>
            <Fact label="Channel">
              {u.channel}
              {u.channelOverride && (
                <Typography variant="caption" color="text.secondary">
                  {" "}
                  (this box)
                </Typography>
              )}
            </Fact>
          </Grid>
          <Grid item xs={6}>
            <Fact label="Last update">
              {u.status ? (
                <StatusChip status={u.status} tone={tone} label={`${u.status}${u.target ? ` to ${u.target}` : ""}`} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  none yet
                </Typography>
              )}
            </Fact>
          </Grid>
          <Grid item xs={6}>
            <Fact label="Window">{u.window}</Fact>
          </Grid>
        </Grid>
        {u.detail && (
          <Typography variant="caption" color="text.secondary">
            {u.detail}
            {u.finishedAt ? ` (${ago(u.finishedAt)})` : ""}
          </Typography>
        )}
        {paused && (
          <Alert severity="warning" sx={{ py: 0 }}>
            Updates paused until {new Date(u.pausedUntil!).toLocaleDateString()}
            {u.pausedReason ? `: ${u.pausedReason}` : ""}
          </Alert>
        )}
        {u.updateNowAt && !u.status?.startsWith("updat") && (
          <Alert severity="info" sx={{ py: 0 }}>
            Update requested; the box applies it at its next check (within 10 minutes) when idle.
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          The box asks Jupiter every 10 minutes and updates itself inside the window, never during an exam or a
          transfer, then checks its own health and rolls back if that fails.
        </Typography>
        {msg && (
          <Alert severity="error" sx={{ py: 0 }}>
            {msg}
          </Alert>
        )}
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<Lightning size={14} />}
            disabled={busy || !!edge.revokedAt}
            onClick={() => post({ updateNow: true })}
          >
            Update now
          </Button>
          {paused ? (
            <Button size="small" color="inherit" disabled={busy} onClick={() => post({ pausedUntil: null })}>
              Resume updates
            </Button>
          ) : (
            <Button
              size="small"
              color="inherit"
              disabled={busy}
              onClick={() =>
                setPause({ until: new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10), reason: "" })
              }
            >
              Pause updates
            </Button>
          )}
          {me?.isOps && (
            <TextField
              select
              size="small"
              label="Channel"
              value={u.channelOverride ?? ""}
              sx={{ minWidth: 160 }}
              disabled={busy}
              onChange={(e) => post({ channel: e.target.value || null })}
            >
              <MenuItem value="">practice default ({u.channel})</MenuItem>
              {CHANNELS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
        {pause && (
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <TextField
              size="small"
              type="date"
              label="Until"
              value={pause.until}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setPause({ ...pause, until: e.target.value })}
            />
            <TextField
              size="small"
              label="Reason"
              placeholder="e.g. surveyor visit"
              value={pause.reason}
              onChange={(e) => setPause({ ...pause, reason: e.target.value })}
              sx={{ minWidth: 220 }}
            />
            <Button
              size="small"
              variant="contained"
              disabled={busy || !pause.until}
              onClick={() =>
                post({ pausedUntil: new Date(`${pause.until}T23:59:59`).toISOString(), pausedReason: pause.reason })
              }
            >
              Pause
            </Button>
            <Button size="small" color="inherit" onClick={() => setPause(null)}>
              Cancel
            </Button>
          </Stack>
        )}
      </Stack>
    </Panel>
  );
}

function PendingActions({ edge }: { edge: Detail }) {
  return (
    <Panel title="Pending actions" icon={<ListChecks size={16} />}>
      {edge.pendingActions.length ? (
        <Stack spacing={1}>
          {edge.pendingActions.map((a) => (
            <Stack key={a.id} direction="row" spacing={1.5} alignItems="center">
              <ConnectionDots state={edge.online ? "connecting" : "waiting"} dots={3} size={3} />
              <Chip size="small" label={a.action} sx={{ fontFamily: fonts.mono, fontSize: "0.7rem" }} />
              <Typography variant="caption" color="text.secondary">
                requested {ago(a.requestedAt)}
              </Typography>
            </Stack>
          ))}
          {!edge.online && (
            <Typography variant="caption" color="text.secondary">
              The edge is offline; these run when it checks in.
            </Typography>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ ...caps, fontSize: 10, letterSpacing: "0.16em" }}>
          Nothing queued
        </Typography>
      )}
    </Panel>
  );
}

/** Instrument video the box is pulling right now, as last reported (every 60 s). */
function LiveStreams({ edge }: { edge: Detail }) {
  const { token } = useSession();
  const [quality, setQuality] = useState<
    { aet: string | null; kind: string; at: string; level: string; message: string }[]
  >([]);
  useEffect(() => {
    const load = () =>
      http.get<typeof quality>(token, `/admin/edges/${edge.id}/quality`).then((r) => r.ok && setQuality(r.result));
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [token, edge.id]);
  const streams = edge.health?.streams ?? [];
  if (!streams.length && !quality.length) return null;
  return (
    <Panel title="Live video from instruments" icon={<WifiHigh size={16} />}>
      <Stack spacing={0.75}>
        {streams.map((st) => (
          <Stack key={st.name} direction="row" spacing={1.5} alignItems="baseline">
            <Mono>{st.name}</Mono>
            <Typography variant="body2" color={st.ready ? "text.primary" : "text.secondary"}>
              {!st.ready
                ? "not receiving from the instrument"
                : st.kbps === null
                ? "receiving (rate on the next report)"
                : st.kbps < 300
                ? `receiving only ${st.kbps} kbps: the instrument is barely sending (camera idle or not in live mode?)`
                : `receiving ${(st.kbps / 1000).toFixed(1)} Mbps`}
            </Typography>
          </Stack>
        ))}
        <Typography variant="caption" color="text.secondary">
          As reported by the box{" "}
          {edge.health?.reportedAt ? `at ${new Date(edge.health.reportedAt).toLocaleTimeString()}` : ""}.
        </Typography>
        {quality.map((q) => (
          <Box key={`${q.aet}:${q.kind}`} sx={{ pt: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              {q.kind === "quality" ? "Measured at Jupiter (last minute)" : "Measured in a viewer's browser"} ·{" "}
              {new Date(q.at).toLocaleTimeString()}
            </Typography>
            <Typography variant="body2" color={q.level === "warn" ? tones.ochre : "text.primary"}>
              {q.message}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Panel>
  );
}

/** Ask the box for its full logs (agent, video, DICOM, service), redacted, and download them here. */
function CollectedLogs({ edge }: { edge: Detail }) {
  const { token } = useSession();
  const [rows, setRows] = useState<
    { id: string; requestedAt: string; doneAt: string | null; result: string | null; url: string | null }[]
  >([]);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    const r = await http.get<typeof rows>(token, `/admin/edges/${edge.id}/logs`);
    if (r.ok) setRows(r.result);
  }, [token, edge.id]);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);
  const collect = async () => {
    setErr("");
    const r = await http.post(token, `/admin/edges/${edge.id}/actions`, { action: "collect-logs" });
    if (!r.ok) setErr(r.error);
    load();
  };
  return (
    <Panel title="Logs from this Pandia" icon={<ListChecks size={16} />}>
      <Stack spacing={1.25}>
        <Typography variant="body2" color="text.secondary">
          Collects the last 2 MB of every log on the box (Pandia, video, DICOM, Windows service), with passwords and
          tokens removed, so nobody at the clinic has to find and send files. Arrives within about 30 seconds.
        </Typography>
        {err && <Alert severity="error">{err}</Alert>}
        <Box>
          <Button variant="outlined" size="small" onClick={collect} disabled={!!edge.revokedAt}>
            Collect logs
          </Button>
        </Box>
        {rows.map((r) => (
          <Stack key={r.id} direction="row" spacing={1.5} alignItems="baseline">
            <Typography variant="body2">{new Date(r.requestedAt).toLocaleString()}</Typography>
            {r.url ? (
              <Link href={r.url} underline="hover">
                Download
              </Link>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {r.doneAt ? r.result : "waiting for the box"}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </Panel>
  );
}
